import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllAdherent,
  getAdherentsCountByGroup,
  getAdherentsCountBySexe,
} from "../../../../../services/adherentServices";
import { getGroupsHome } from "../../../../../services/groupServices";
import {
  getAllMessagesContactForm,
  markMessageContactForm,
  deleteMessageContactForm,
} from "../../../../../services/contactformServices";
import {
  getAllNotifications,
  deleteNotificationById,
  markNotificationAsRead,
} from "../../../../../services/notificationServices";
import {
  countVisitesByDate,
  deleteVisitesByDate,
} from "../../../../../services/visitesServices";
import { getSettings } from "../../../../../services/settingsServices";

import { useNotification } from "../../../../../contexts/NotificationContext";
import { useContactForm } from "../../../../../contexts/ContactFormContext";

import {
  getGroupColorMap,
  getRecentAdherents,
  isDemoUser,
  safeJsonParse,
} from "../utils/dashboardUtils";

const DEFAULT_CLUB = {
  nom: "Votre club",
  logo: "",
};

const DEFAULT_USER = {
  id: 0,
  nom: "",
  prenom: "",
  email: "",
  photoURL: "",
  role_id: 0,
};

const initialState = {
  clubInfo: DEFAULT_CLUB,
  userInfo: DEFAULT_USER,
  adherents: [],
  groups: [],
  groupDistribution: [],
  genderDistribution: [],
  visits: [],
  messages: [],
  notifications: [],
};

const getSettledValue = (result, fallback) => {
  if (result.status === "fulfilled") return result.value || fallback;
  return fallback;
};

export default function useAdminDashboard() {
  const { setUnreadCount } = useNotification();
  const { setUnreadCountContactForm } = useContactForm();

  const [state, setState] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingVisits, setDeletingVisits] = useState(false);

  const isDemoMode = useMemo(
    () => isDemoUser(state.userInfo),
    [state.userInfo]
  );

  const updateUnreadCounters = useCallback(
    ({ nextMessages, nextNotifications }) => {
      if (Array.isArray(nextMessages)) {
        const unreadMessages = nextMessages.filter(
          (message) => !message.read_message
        ).length;

        setUnreadCountContactForm(unreadMessages);
      }

      if (Array.isArray(nextNotifications)) {
        const unreadNotifications = nextNotifications.filter(
          (notification) => !notification.read_notification
        ).length;

        setUnreadCount(unreadNotifications);
      }
    },
    [setUnreadCount, setUnreadCountContactForm]
  );

  const loadDashboard = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const storedUser =
          safeJsonParse(localStorage.getItem("user"), null) ||
          safeJsonParse(localStorage.getItem("userInfo"), null) ||
          DEFAULT_USER;

        const [
          settingsResult,
          adherentsResult,
          groupsResult,
          groupDistributionResult,
          genderDistributionResult,
          visitsResult,
          messagesResult,
          notificationsResult,
        ] = await Promise.allSettled([
          getSettings(),
          getAllAdherent(),
          getGroupsHome(),
          getAdherentsCountByGroup(),
          getAdherentsCountBySexe(),
          countVisitesByDate(),
          getAllMessagesContactForm(),
          getAllNotifications(),
        ]);

        const settings = getSettledValue(settingsResult, []);
        const club = Array.isArray(settings) ? settings[0] : settings;

        const nextMessages = getSettledValue(messagesResult, []);
        const nextNotifications = getSettledValue(notificationsResult, []);

        setState({
          clubInfo: {
            nom: club?.nom_club || DEFAULT_CLUB.nom,
            logo: club?.logo_club || "",
          },
          userInfo: {
            ...DEFAULT_USER,
            ...storedUser,
          },
          adherents: getSettledValue(adherentsResult, []),
          groups: getSettledValue(groupsResult, []),
          groupDistribution: getSettledValue(groupDistributionResult, []),
          genderDistribution: getSettledValue(genderDistributionResult, []),
          visits: getSettledValue(visitsResult, []),
          messages: nextMessages,
          notifications: nextNotifications,
        });

        updateUnreadCounters({
          nextMessages,
          nextNotifications,
        });

        const hasRejected = [
          settingsResult,
          adherentsResult,
          groupsResult,
          groupDistributionResult,
          genderDistributionResult,
          visitsResult,
          messagesResult,
          notificationsResult,
        ].some((result) => result.status === "rejected");

        if (hasRejected && !silent) {
          toast.warning(
            "Certaines informations du tableau de bord n'ont pas pu être chargées."
          );
        }
      } catch (error) {
        console.error("Erreur lors du chargement du dashboard :", error);
        toast.error("Erreur lors du chargement du tableau de bord.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [updateUnreadCounters]
  );

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const refreshDashboard = useCallback(() => {
    loadDashboard({ silent: true });
  }, [loadDashboard]);

  const guardDemoMode = useCallback(() => {
    if (!isDemoMode) return false;

    toast.info("Fonction désactivée en mode démonstration.");
    return true;
  }, [isDemoMode]);

  const toggleMessageRead = useCallback(
    async (messageId) => {
      if (guardDemoMode()) return;

      const targetMessage = state.messages.find(
        (message) => message.id === messageId
      );

      if (!targetMessage) return;

      try {
        await markMessageContactForm(messageId);

        const nextMessages = state.messages.map((message) =>
          message.id === messageId
            ? {
                ...message,
                read_message: !message.read_message,
              }
            : message
        );

        setState((prev) => ({
          ...prev,
          messages: nextMessages,
        }));

        updateUnreadCounters({
          nextMessages,
          nextNotifications: state.notifications,
        });
      } catch (error) {
        console.error("Erreur lors de la mise à jour du message :", error);
        toast.error("Impossible de modifier l'état du message.");
      }
    },
    [guardDemoMode, state.messages, state.notifications, updateUnreadCounters]
  );

  const deleteMessage = useCallback(
    async (messageId) => {
      if (guardDemoMode()) return;

      try {
        await deleteMessageContactForm(messageId);

        const nextMessages = state.messages.filter(
          (message) => message.id !== messageId
        );

        setState((prev) => ({
          ...prev,
          messages: nextMessages,
        }));

        updateUnreadCounters({
          nextMessages,
          nextNotifications: state.notifications,
        });

        toast.success("Message supprimé avec succès.");
      } catch (error) {
        console.error("Erreur lors de la suppression du message :", error);
        toast.error("Impossible de supprimer le message.");
      }
    },
    [guardDemoMode, state.messages, state.notifications, updateUnreadCounters]
  );

  const toggleNotificationRead = useCallback(
    async (notificationId) => {
      if (guardDemoMode()) return;

      const targetNotification = state.notifications.find(
        (notification) => notification.id === notificationId
      );

      if (!targetNotification) return;

      try {
        await markNotificationAsRead(notificationId);

        const nextNotifications = state.notifications.map((notification) =>
          notification.id === notificationId
            ? {
                ...notification,
                read_notification: !notification.read_notification,
              }
            : notification
        );

        setState((prev) => ({
          ...prev,
          notifications: nextNotifications,
        }));

        updateUnreadCounters({
          nextMessages: state.messages,
          nextNotifications,
        });
      } catch (error) {
        console.error(
          "Erreur lors de la mise à jour de la notification :",
          error
        );
        toast.error("Impossible de modifier l'état de la notification.");
      }
    },
    [guardDemoMode, state.messages, state.notifications, updateUnreadCounters]
  );

  const deleteNotification = useCallback(
    async (notificationId) => {
      if (guardDemoMode()) return;

      try {
        await deleteNotificationById(notificationId);

        const nextNotifications = state.notifications.filter(
          (notification) => notification.id !== notificationId
        );

        setState((prev) => ({
          ...prev,
          notifications: nextNotifications,
        }));

        updateUnreadCounters({
          nextMessages: state.messages,
          nextNotifications,
        });

        toast.success("Notification supprimée avec succès.");
      } catch (error) {
        console.error(
          "Erreur lors de la suppression de la notification :",
          error
        );
        toast.error("Impossible de supprimer la notification.");
      }
    },
    [guardDemoMode, state.messages, state.notifications, updateUnreadCounters]
  );

  const deleteVisitsRange = useCallback(
    async ({ dateDebut, dateFin }) => {
      if (guardDemoMode()) return;

      if (!dateDebut || !dateFin) {
        toast.warning("Veuillez sélectionner une date de début et une date de fin.");
        return;
      }

      if (new Date(dateDebut) > new Date(dateFin)) {
        toast.warning("La date de début doit être antérieure à la date de fin.");
        return;
      }

      setDeletingVisits(true);

      try {
        const response = await deleteVisitesByDate(dateDebut, dateFin);
        const nextVisits = await countVisitesByDate();

        setState((prev) => ({
          ...prev,
          visits: nextVisits || [],
        }));

        toast.success(
          response?.message || "Les visites sélectionnées ont été supprimées."
        );
      } catch (error) {
        console.error("Erreur lors de la suppression des visites :", error);
        toast.error("Impossible de supprimer les visites.");
      } finally {
        setDeletingVisits(false);
      }
    },
    [guardDemoMode]
  );

  const recentAdherents = useMemo(
    () => getRecentAdherents(state.adherents, 8),
    [state.adherents]
  );

  const groupColorMap = useMemo(
    () => getGroupColorMap(state.groups),
    [state.groups]
  );

  const unreadMessages = useMemo(
    () => state.messages.filter((message) => !message.read_message).length,
    [state.messages]
  );

  const unreadNotifications = useMemo(
    () =>
      state.notifications.filter(
        (notification) => !notification.read_notification
      ).length,
    [state.notifications]
  );

  return {
    loading,
    refreshing,
    deletingVisits,
    isDemoMode,
    ...state,
    recentAdherents,
    groupColorMap,
    unreadMessages,
    unreadNotifications,
    actions: {
      refreshDashboard,
      toggleMessageRead,
      deleteMessage,
      toggleNotificationRead,
      deleteNotification,
      deleteVisitsRange,
    },
  };
}